/**
 * PreferencesSheet Component
 *
 * BottomSheet content with 3 preference questions and pill selectors.
 * Q1: Experience level (single-select, radiogroup)
 * Q2: Improvement goals (multi-select, group with aria-pressed)
 * Q3: Daily training time (single-select, radiogroup)
 *
 * Save requires all 3 questions to have at least one selection.
 * Uses existing BottomSheet with snapTo='full'.
 */

import { useState, useCallback, useEffect } from 'react';
import { BottomSheet } from './BottomSheet';
import { cn } from '../../../lib/utils';
import type { OperatorPreferences } from '../hooks/usePreferences';

interface PreferencesSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Unique SOP names derived from operator's training data */
  sopNames: string[];
  onSave: (prefs: OperatorPreferences) => void;
}

// ============================================
// OPTIONS
// ============================================

const EXPERIENCE_OPTIONS = [
  { label: 'New', value: 'new' },
  { label: '<6 months', value: 'less_than_6_months' },
  { label: '6-12 months', value: '6_to_12_months' },
  { label: '1+ years', value: 'over_1_year' },
] as const;

const GENERIC_GOALS = ['Safety', 'Efficiency', 'Compliance'];

const TIME_OPTIONS = [
  { label: '5 min', value: 5 },
  { label: '10 min', value: 10 },
  { label: '15 min', value: 15 },
] as const;

export function PreferencesSheet({
  isOpen,
  onClose,
  sopNames,
  onSave,
}: PreferencesSheetProps) {
  const [experienceLevel, setExperienceLevel] = useState<string | null>(null);
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [dailyTime, setDailyTime] = useState<number | null>(null);

  // Reset answers when sheet closes so re-opening always starts fresh.
  // BottomSheet keeps children mounted during the exit animation, which
  // would otherwise preserve partial answers across open/close cycles.
  useEffect(() => {
    if (!isOpen) {
      setExperienceLevel(null);
      setSelectedGoals([]);
      setDailyTime(null);
    }
  }, [isOpen]);

  const goalOptions = sopNames.length > 0 ? sopNames : GENERIC_GOALS;

  const toggleGoal = useCallback((goal: string) => {
    setSelectedGoals((prev) =>
      prev.includes(goal)
        ? prev.filter((g) => g !== goal)
        : [...prev, goal],
    );
  }, []);

  // All 3 questions must have at least one selection
  const canSave =
    experienceLevel !== null &&
    selectedGoals.length > 0 &&
    dailyTime !== null;

  const handleSave = useCallback(() => {
    if (!canSave || experienceLevel === null || dailyTime === null) return;
    onSave({
      experienceLevel,
      goals: selectedGoals,
      dailyTimeMinutes: dailyTime,
    });
  }, [canSave, experienceLevel, selectedGoals, dailyTime, onSave]);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Personalize Training"
      snapTo="full"
    >
      <div className="flex flex-col gap-6 pb-4">
        {/* Q1: Experience Level */}
        <div>
          <p
            id="q1-label"
            className="text-sm font-semibold text-op-text-primary"
          >
            How long have you been in this role?
          </p>
          <div
            role="radiogroup"
            aria-labelledby="q1-label"
            className="mt-2 flex flex-wrap gap-2"
          >
            {EXPERIENCE_OPTIONS.map((opt) => {
              const selected = experienceLevel === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={opt.label}
                  className={cn(
                    'min-h-[44px] whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150',
                    selected
                      ? 'bg-guild-green text-white'
                      : 'bg-gray-100 text-op-text-secondary active:bg-gray-200',
                    'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
                  )}
                  onClick={() => setExperienceLevel(opt.value)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Q2: Improvement Goals */}
        <div>
          <p
            id="q2-label"
            className="text-sm font-semibold text-op-text-primary"
          >
            What do you want to improve?
          </p>
          <p className="mt-0.5 text-xs text-op-text-tertiary">
            Select all that apply
          </p>
          <div
            role="group"
            aria-labelledby="q2-label"
            className="mt-2 flex flex-wrap gap-2"
          >
            {goalOptions.map((goal) => {
              const selected = selectedGoals.includes(goal);
              return (
                <button
                  key={goal}
                  type="button"
                  aria-pressed={selected}
                  aria-label={goal}
                  className={cn(
                    'min-h-[44px] whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150',
                    selected
                      ? 'bg-guild-green text-white'
                      : 'bg-gray-100 text-op-text-secondary active:bg-gray-200',
                    'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
                  )}
                  onClick={() => toggleGoal(goal)}
                >
                  {goal}
                </button>
              );
            })}
          </div>
        </div>

        {/* Q3: Daily Training Time */}
        <div>
          <p
            id="q3-label"
            className="text-sm font-semibold text-op-text-primary"
          >
            How much daily training time works for you?
          </p>
          <div
            role="radiogroup"
            aria-labelledby="q3-label"
            className="mt-2 flex flex-wrap gap-2"
          >
            {TIME_OPTIONS.map((opt) => {
              const selected = dailyTime === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={opt.label}
                  className={cn(
                    'min-h-[44px] whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors duration-150',
                    selected
                      ? 'bg-guild-green text-white'
                      : 'bg-gray-100 text-op-text-secondary active:bg-gray-200',
                    'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
                  )}
                  onClick={() => setDailyTime(opt.value)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Save Button */}
        <button
          type="button"
          disabled={!canSave}
          onClick={handleSave}
          className={cn(
            'min-h-[48px] w-full rounded-xl text-sm font-semibold transition-colors duration-150',
            canSave
              ? 'bg-guild-green text-white active:bg-guild-green/90'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed',
            'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
          )}
        >
          Save Preferences
        </button>
      </div>
    </BottomSheet>
  );
}
